import React, { memo, useCallback, useMemo } from 'react';

import { Alert, Box, Button, Snackbar, Typography } from '@mui/material';
import PropTypes from 'prop-types';

import { StatusEnum } from 'constants/status';

import StatusIconCDS from '../../StatusIconCDS';

import { getAlertSlotProps, toastOptions } from './AlertCDS.constants';
import {
  actionButtonStyles,
  actionsContainerStyles,
  getAlertStyles,
  getSnackbarStyles,
} from './AlertCDS.styles';
import { AlertCDSTestIds } from './AlertCDS.testIds';

const AlertCDS = ({
  message,
  variant = StatusEnum.INFO,
  title,
  id,
  withCloseButton = true,
  actions,
  onClose,
}) => {
  const handleClose = useCallback(() => onClose?.(id), [onClose, id]);

  // Action buttons render inside the alert body (not MUI Alert's `action` slot,
  // which would displace the close button). Acting dismisses the toast.
  const actionsWithDismiss = useMemo(
    () =>
      actions?.map(action => ({
        ...action,
        onClick: () => {
          action.onClick?.();
          handleClose();
        },
      })),
    [actions, handleClose],
  );

  return (
    <Snackbar open sx={getSnackbarStyles({ variant })} {...toastOptions} key={id}>
      <Alert
        icon={<StatusIconCDS type={variant} />}
        severity={variant}
        slotProps={getAlertSlotProps({ onClose: handleClose, variant })}
        sx={getAlertStyles({ variant })}
        onClose={withCloseButton ? handleClose : undefined}
      >
        {title && (
          <Typography color={`${variant}.dark`} variant="bold16">
            {title}
          </Typography>
        )}
        <Typography color="text.dark" data-testid={AlertCDSTestIds.MESSAGE} variant="regular14">
          {Array.isArray(message)
            ? message.map((text, index, arr) => (
                <React.Fragment key={index}>
                  {text}
                  {arr.length - 1 !== index && <br />}
                </React.Fragment>
              ))
            : message}
        </Typography>
        {!!actionsWithDismiss?.length && (
          <Box sx={actionsContainerStyles}>
            {actionsWithDismiss.map(action => (
              <Button
                data-testid={`${AlertCDSTestIds.ACTION_BUTTON}-${action.id}`}
                key={action.id}
                size="small"
                sx={actionButtonStyles}
                variant="text"
                onClick={action.onClick}
              >
                {action.label}
              </Button>
            ))}
          </Box>
        )}
      </Alert>
    </Snackbar>
  );
};

AlertCDS.propTypes = {
  message: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]).isRequired,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onClose: PropTypes.func.isRequired,
  variant: PropTypes.string,
  title: PropTypes.string,
  withCloseButton: PropTypes.bool,
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      onClick: PropTypes.func,
    }),
  ),
};

export default memo(AlertCDS);
